import { ChevronLeft, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button, Card } from "../../../../components";
import PageHeader from "../../../../components/common/PageHeader";
import {
  districtReach,
  engagementLadder,
  portfolioKpis,
  portfolioRecommendations,
  programComparison,
  residentsAtRisk,
} from "../data/programPortfolioReport.data";

export default function ProgramPerformanceReportPage() {
  const navigate = useNavigate();

  return (
    <main className="min-h-full bg-[#effbf3] px-4 py-6 text-[#004b3a] sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1440px] space-y-6">
        <PageHeader
          title="Program Portfolio"
          subtitle={
            <span>
              <span className="font-bold uppercase tracking-[0.12em] text-[#008a72]">
                Director view · Last 12 months
              </span>
              <br />
              Where residents engage, return, and need a better next step.
            </span>
          }
          actions={
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate("/reports")}
              >
                <ChevronLeft />
                Reports
              </Button>
              <Button
                type="button"
                variant="brand-outline"
                onClick={() => window.print()}
              >
                <ExternalLink />
                Export report
              </Button>
            </div>
          }
        />
        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {portfolioKpis.map((kpi) => (
            <Card
              className="rounded-3xl border border-[#dbe9e1] bg-white p-5 shadow-sm"
              key={kpi.label}
            >
              <p className="text-sm font-bold text-[#587268]">{kpi.label}</p>
              <strong className="mt-2 block text-3xl">{kpi.value}</strong>
              <span
                className={`mt-2 block text-xs font-semibold ${kpi.tone === "warning" ? "text-[#a96300]" : "text-[#007b67]"}`}
              >
                {kpi.note}
              </span>
            </Card>
          ))}
        </section>
        <section className="grid gap-6 xl:grid-cols-[minmax(0,1.1fr)_minmax(360px,.9fr)]">
          <Card className="rounded-3xl border border-[#dbe9e1] bg-white p-5 shadow-sm sm:p-6">
            <h2 className="text-xl font-bold">Engagement ladder</h2>
            <p className="mt-2 text-sm text-[#587268]">
              The largest loss is between a first attendance and a second program.
            </p>
            <div className="mt-6 space-y-5">
              {engagementLadder.map((stage) => (
                <article
                  className="grid gap-2 sm:grid-cols-[190px_minmax(0,1fr)_80px] sm:items-center"
                  key={stage.label}
                >
                  <div>
                    <strong className="block text-sm">{stage.label}</strong>
                    <span className="text-xs text-[#587268]">{stage.value} residents</span>
                  </div>
                  <div className="h-4 overflow-hidden rounded-full bg-[#e4f0e8]">
                    <i
                      className="block h-full rounded-full bg-[#00a884]"
                      style={{ width: `${stage.rate}%` }}
                    />
                  </div>
                  <span className="text-right text-sm font-bold">{stage.rate}%</span>
                </article>
              ))}
            </div>
          </Card>
          <Card className="rounded-3xl border border-[#dbe9e1] bg-white p-5 shadow-sm sm:p-6">
            <h2 className="text-xl font-bold">Reach by council district</h2>
            <p className="mt-2 text-sm text-[#587268]">
              Participation rate among addressable residents.
            </p>
            <div className="mt-6 space-y-5">
              {districtReach.map((district) => (
                <article key={district.district}>
                  <div className="flex justify-between gap-3 text-sm">
                    <strong>{district.district}</strong>
                    <span>
                      {district.residents} residents · <b>{district.rate}%</b>
                    </span>
                  </div>
                  <div className="mt-2 h-3 overflow-hidden rounded-full bg-[#e4f0e8]">
                    <i
                      className={`block h-full rounded-full ${district.rate < 22 ? "bg-[#f1ab38]" : "bg-[#008a72]"}`}
                      style={{ width: `${district.rate * 2.5}%` }}
                    />
                  </div>
                </article>
              ))}
            </div>
          </Card>
        </section>
        <Card className="rounded-3xl border border-[#dbe9e1] bg-white p-5 shadow-sm sm:p-6">
          <h2 className="text-xl font-bold">Program comparison</h2>
          <p className="mt-2 text-sm text-[#587268]">
            Use attendance, return, and cross-program participation together before making portfolio
            decisions.
          </p>
          <div className="mt-5 overflow-x-auto">
            <table className="w-full min-w-[1040px] text-left text-sm">
              <thead className="border-b text-xs uppercase text-[#587268]">
                <tr>
                  <th className="pb-3">Program</th>
                  <th className="pb-3">Residents</th>
                  <th className="pb-3">Attendance</th>
                  <th className="pb-3">Return</th>
                  <th className="pb-3">2+ programs</th>
                  <th className="pb-3">Satisfaction</th>
                  <th className="pb-3">Recommended next step</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#edf3ef]">
                {programComparison.map((program) => (
                  <tr key={program.program}>
                    <td className="py-4">
                      <strong className="block">{program.program}</strong>
                      <span className="text-xs text-[#587268]">{program.department}</span>
                    </td>
                    <td className="py-4 font-semibold">{program.residents}</td>
                    <td className="py-4 font-semibold">{program.attendance}</td>
                    <td className="py-4 font-semibold">{program.returnRate}</td>
                    <td className="py-4 font-semibold">{program.secondProgram}</td>
                    <td className="py-4 font-semibold">{program.satisfaction}</td>
                    <td className="py-4 font-bold text-[#007b67]">{program.action}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        <section className="grid gap-6 xl:grid-cols-2">
          <Card className="rounded-3xl border border-[#dbe9e1] bg-white p-5 shadow-sm sm:p-6">
            <h2 className="text-xl font-bold">Residents at risk of losing connection</h2>
            <div className="mt-5 divide-y divide-[#edf3ef]">
              {residentsAtRisk.map((group) => (
                <article
                  className="py-4"
                  key={group.group}
                >
                  <div className="flex flex-wrap justify-between gap-2">
                    <h3 className="font-bold">{group.group}</h3>
                    <span className="rounded-full bg-[#fff0cf] px-3 py-1 text-xs font-bold text-[#805200]">
                      {group.residents}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-[#587268]">{group.signal}</p>
                  <p className="mt-2 text-sm font-bold text-[#006f5a]">Try: {group.action}</p>
                </article>
              ))}
            </div>
          </Card>
          <section className="rounded-3xl bg-[#004b3a] p-6 text-white">
            <p className="text-xs font-bold uppercase tracking-[0.1em] text-[#f9c75c]">
              What&apos;s driving it
            </p>
            <h2 className="mt-3 text-2xl font-bold">
              Programs are bringing residents in; the portfolio needs a more deliberate return path.
            </h2>
            <ul className="mt-6 space-y-4">
              {portfolioRecommendations.map((recommendation) => (
                <li
                  className="rounded-2xl bg-[#00664f] p-4 text-sm font-semibold text-[#e6f6ee]"
                  key={recommendation}
                >
                  {recommendation}
                </li>
              ))}
            </ul>
          </section>
        </section>
      </div>
    </main>
  );
}
